/**
 * Noun / adjective / article agreement in gender and number, with the exception classes that
 * actually bite (BRIEF <spanish_correctness> layer 2, SPEC §2 `lang/agreement.ts`).
 *
 * Pure. Rules plus a short list of genuine exceptions — no dictionary, and no pretence of one.
 * `guessGender` reports how it knows, and `unknown` is a real answer: an item whose gender the
 * rules cannot settle carries it in the content instead, which is honest and cheap.
 */

import { accentSyllable, analyseStress, stripAccents, syllabify } from './accents';

export type Gender = 'm' | 'f';
export type GrammaticalNumber = 'sg' | 'pl';

// ---------------------------------------------------------------------------------------------
// Gender
// ---------------------------------------------------------------------------------------------

/** Feminine despite ending in -o. Short, closed, and almost all clippings of longer words. */
const FEMININE_IN_O = new Set([
  'mano',
  'foto',
  'moto',
  'radio',
  'disco',
  'libido',
]);

/**
 * Masculine despite ending in -a.
 *
 * Two groups: the Greek `-ma` nouns, which are numerous and productive enough to be worth teaching
 * as a class, and a handful of one-offs.
 */
const MASCULINE_IN_A = new Set([
  'día',
  'mapa',
  'planeta',
  'sofá',
  'tranvía',
  'problema',
  'sistema',
  'programa',
  'idioma',
  'tema',
  'clima',
  'drama',
  'poema',
  'diploma',
  'síntoma',
]);

const FEMININE_SUFFIXES = ['ción', 'sión', 'dad', 'tad', 'tud', 'umbre', 'ez', 'eza', 'cia'];
const MASCULINE_SUFFIXES = ['aje', 'or', 'ambre', 'án'];

export type GenderEvidence =
  /** A reliable ending decided it. */
  | 'suffix'
  /** The default -o / -a ending decided it. */
  | 'ending'
  /** It is on one of the exception lists. */
  | 'exception'
  /** Nothing decided it; the content has to carry the gender. */
  | 'unknown';

export interface GenderGuess {
  gender: Gender | null;
  evidence: GenderEvidence;
  /** Plain feedback text. Never scolding, and never claiming more certainty than there is. */
  reason: string;
}

/**
 * The gender of a noun, and how confidently.
 *
 * `null` with `unknown` is a real and frequent answer — `el lápiz`, `la clase` and `el coche` are
 * not derivable from their endings, and guessing at them would be worse than declining, because a
 * confident wrong gender in feedback teaches the error.
 */
export function guessGender(noun: string): GenderGuess {
  const word = noun.trim().toLowerCase();

  if (FEMININE_IN_O.has(word)) {
    return { gender: 'f', evidence: 'exception', reason: `${word} is feminine despite ending in -o.` };
  }
  if (MASCULINE_IN_A.has(word)) {
    const greek = word.endsWith('ma');
    return {
      gender: 'm',
      evidence: 'exception',
      reason: greek
        ? `${word} is masculine: the -ma nouns come from Greek and keep masculine gender.`
        : `${word} is masculine despite ending in -a.`,
    };
  }

  for (const suffix of FEMININE_SUFFIXES) {
    if (word.endsWith(suffix)) {
      return { gender: 'f', evidence: 'suffix', reason: `Nouns ending in -${suffix} are feminine.` };
    }
  }
  for (const suffix of MASCULINE_SUFFIXES) {
    if (word.endsWith(suffix)) {
      return {
        gender: 'm',
        evidence: 'suffix',
        reason: `Nouns ending in -${suffix} are masculine.`,
      };
    }
  }

  const last = stripAccents(word).at(-1);
  if (last === 'o') return { gender: 'm', evidence: 'ending', reason: 'Nouns ending in -o are usually masculine.' };
  if (last === 'a') return { gender: 'f', evidence: 'ending', reason: 'Nouns ending in -a are usually feminine.' };

  return {
    gender: null,
    evidence: 'unknown',
    reason: 'This ending does not settle the gender — it has to be learned with the word.',
  };
}

/**
 * Feminine nouns beginning with a **stressed** a- or ha- take `el`/`un` in the singular, purely to
 * avoid two a-sounds colliding. They stay feminine: `el agua fría`, `las aguas`.
 *
 * This is the exception learners meet first and mis-learn hardest, because the article looks
 * masculine and the adjective does not.
 */
export function takesElForEuphony(noun: string, gender: Gender | null): boolean {
  if (gender !== 'f') return false;
  const word = stripAccents(noun.trim().toLowerCase());
  if (!word.startsWith('a') && !word.startsWith('ha')) return false;
  const analysis = analyseStress(noun.trim().toLowerCase());
  return analysis.stressIndex === 0;
}

// ---------------------------------------------------------------------------------------------
// Number
// ---------------------------------------------------------------------------------------------

/**
 * The plural, with the written accent re-derived rather than tabulated.
 *
 * Adding a syllable does not move the stress, but it does change which rule applies to it, so a
 * word can gain or lose its tilde: `joven` → `jóvenes`, `canción` → `canciones`. The stressed
 * syllable index is taken from the singular and re-applied to the plural.
 */
export function pluralize(word: string): string {
  const lower = word.trim();
  if (lower.length === 0) return lower;

  const bare = stripAccents(lower);
  const last = bare.at(-1)!;
  const stressIndex = analyseStress(lower).stressIndex;

  // Unstressed final -s or -x: invariable. `el lunes` / `los lunes`, `la crisis` / `las crisis`.
  if ((last === 's' || last === 'x') && !isStressedOnLastSyllable(lower)) return lower;

  let plural: string;
  if (last === 'z') {
    // The z/c alternation: `lápiz` → `lápices`, `vez` → `veces`.
    plural = `${bare.slice(0, -1)}ces`;
  } else if ('aeiou'.includes(last)) {
    plural = `${bare}s`;
  } else if (last === 'í' || last === 'ú') {
    plural = `${bare}es`;
  } else {
    plural = `${bare}es`;
  }

  return reaccent(plural, stressIndex);
}

function isStressedOnLastSyllable(word: string): boolean {
  const analysis = analyseStress(word);
  return analysis.fromEnd === 0;
}

/** Put the tilde back on the plural if, and only if, the rules ask for it. */
function reaccent(plural: string, stressIndex: number): string {
  const syllables = syllabify(plural);
  const fromEnd = syllables.length - 1 - stressIndex;
  if (fromEnd < 0) return plural;

  const bare = stripAccents(plural);
  const endsInVowelNorS = /[aeiouns]$/.test(bare);
  const needed =
    syllables.length > 1 &&
    (fromEnd >= 2 || (fromEnd === 0 && endsInVowelNorS) || (fromEnd === 1 && !endsInVowelNorS));

  return needed ? accentSyllable(bare, stressIndex) : bare;
}

// ---------------------------------------------------------------------------------------------
// Articles
// ---------------------------------------------------------------------------------------------

export type ArticleKind = 'definite' | 'indefinite';

const ARTICLES: Record<ArticleKind, Record<Gender, Record<GrammaticalNumber, string>>> = {
  definite: {
    m: { sg: 'el', pl: 'los' },
    f: { sg: 'la', pl: 'las' },
  },
  indefinite: {
    m: { sg: 'un', pl: 'unos' },
    f: { sg: 'una', pl: 'unas' },
  },
};

/**
 * The article for a noun, applying the stressed-a euphony rule.
 *
 * Takes the noun rather than just gender and number precisely so that rule can fire — `el agua`
 * cannot be derived from `('f', 'sg')` alone.
 */
export function articleFor(
  noun: string,
  number: GrammaticalNumber,
  kind: ArticleKind = 'definite',
  gender: Gender | null = guessGender(noun).gender,
): string | null {
  if (gender === null) return null;
  if (number === 'sg' && takesElForEuphony(noun, gender)) {
    return kind === 'definite' ? 'el' : 'un';
  }
  return ARTICLES[kind][gender][number];
}

// ---------------------------------------------------------------------------------------------
// Adjectives
// ---------------------------------------------------------------------------------------------

/**
 * Adjective endings that inflect for gender even though they end in a consonant: the `-dor`/`-tor`
 * agents, the `-ón`/`-án`/`-ín` group, and nationalities.
 */
const GENDERED_CONSONANT_ENDINGS = ['dor', 'tor', 'sor', 'ón', 'án', 'ín', 'és'];

/** Nationalities in -l and -z that still inflect: español, andaluz. */
const GENDERED_EXTRA = new Set(['español', 'andaluz', 'francés', 'inglés', 'alemán', 'japonés']);

/**
 * Agree an adjective, given in its masculine singular form.
 *
 * Three classes: `-o` inflects fully; `-e` and most consonants inflect for number only; the
 * agent/nationality endings above inflect for both. Getting the third class wrong is what makes
 * `una mujer trabajadora` come out as `trabajador`, so it is handled rather than approximated.
 */
export function agreeAdjective(
  masculineSingular: string,
  gender: Gender,
  number: GrammaticalNumber,
): string {
  const base = masculineSingular.trim();
  const bare = stripAccents(base).toLowerCase();

  let singular = base;
  if (gender === 'f') {
    if (bare.endsWith('o')) {
      singular = `${base.slice(0, -1)}a`;
    } else if (
      GENDERED_EXTRA.has(base.toLowerCase()) ||
      GENDERED_CONSONANT_ENDINGS.some((ending) => base.toLowerCase().endsWith(ending))
    ) {
      // The tilde on `inglés`/`alemán` exists only because the masculine is an aguda ending in
      // s or n. Adding -a makes it a llana, and the tilde goes.
      singular = `${stripAccents(base)}a`;
    }
  }

  return number === 'sg' ? singular : pluralize(singular);
}

// ---------------------------------------------------------------------------------------------
// Checking
// ---------------------------------------------------------------------------------------------

export type AgreementError = 'gender' | 'number' | 'article' | null;

export interface AgreementCheck {
  ok: boolean;
  error: AgreementError;
  /** The whole phrase as it should read. */
  expected: string;
  reason: string;
}

export interface Phrase {
  article?: string;
  noun: string;
  adjective?: string;
  gender: Gender;
  number: GrammaticalNumber;
}

/**
 * Check a noun phrase against the gender and number it is supposed to carry.
 *
 * Reports **which** kind of disagreement it is — gender, number, or the article alone — because
 * "wrong" is not feedback and the near-miss classifier needs the distinction to name the error.
 */
export function checkPhrase(phrase: Phrase, actual: string): AgreementCheck {
  const expectedArticle =
    phrase.article === undefined
      ? null
      : articleFor(phrase.noun, phrase.number, 'definite', phrase.gender);
  const expectedNoun = phrase.number === 'pl' ? pluralize(phrase.noun) : phrase.noun;
  const expectedAdjective =
    phrase.adjective === undefined
      ? null
      : agreeAdjective(phrase.adjective, phrase.gender, phrase.number);

  const expected = [expectedArticle, expectedNoun, expectedAdjective]
    .filter((part): part is string => part !== null)
    .join(' ');

  const normalised = actual.trim().toLowerCase().replace(/\s+/g, ' ');
  if (normalised === expected.toLowerCase()) {
    return { ok: true, error: null, expected, reason: 'Agrees in gender and number.' };
  }

  const parts = normalised.split(' ');
  const flipped = phrase.gender === 'm' ? 'f' : 'm';
  const otherGender = [
    expectedArticle === null ? null : articleFor(phrase.noun, phrase.number, 'definite', flipped),
    expectedNoun,
    expectedAdjective === null ? null : agreeAdjective(phrase.adjective!, flipped, phrase.number),
  ]
    .filter((part): part is string => part !== null)
    .join(' ');

  if (normalised === otherGender.toLowerCase()) {
    return {
      ok: false,
      error: 'gender',
      expected,
      reason: `${phrase.noun} is ${phrase.gender === 'm' ? 'masculine' : 'feminine'}, so the article and adjective follow it.`,
    };
  }

  const otherNumber: GrammaticalNumber = phrase.number === 'sg' ? 'pl' : 'sg';
  const flippedNumber = [
    expectedArticle === null ? null : articleFor(phrase.noun, otherNumber, 'definite', phrase.gender),
    otherNumber === 'pl' ? pluralize(phrase.noun) : phrase.noun,
    expectedAdjective === null ? null : agreeAdjective(phrase.adjective!, phrase.gender, otherNumber),
  ]
    .filter((part): part is string => part !== null)
    .join(' ');

  if (normalised === flippedNumber.toLowerCase()) {
    return {
      ok: false,
      error: 'number',
      expected,
      reason: `Everything in the phrase has to match ${phrase.number === 'sg' ? 'the singular' : 'the plural'}.`,
    };
  }

  // Same noun and adjective, different article: the euphony rule is the usual culprit.
  if (
    expectedArticle !== null &&
    parts.length === expected.split(' ').length &&
    parts.slice(1).join(' ') === expected.split(' ').slice(1).join(' ').toLowerCase()
  ) {
    return {
      ok: false,
      error: 'article',
      expected,
      reason: takesElForEuphony(phrase.noun, phrase.gender)
        ? `${phrase.noun} is feminine but takes "el" in the singular, to keep two a-sounds apart. The adjective stays feminine.`
        : 'The article has to match the noun in gender and number.',
    };
  }

  return { ok: false, error: null, expected, reason: 'That is not this phrase.' };
}
